import { Box, Button, Grid, IconButton, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom/cjs/react-router-dom'
import Icon from '@mdi/react'
import {
  mdiBarcode,
  mdiCheckCircle,
  mdiCheckCircleOutline,
  mdiCreditCard,
  mdiCreditCardOutline,
  mdiDelete,
  mdiNoteEditOutline,
  mdiPlus,
} from '@mdi/js'
import { format, parse } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import colors from '../../../../assets/colors'
import { renderRoute } from '../../../../util/utils'
import {
  cancelSubscription,
  destroyCard,
  fetchPlanDetails,
  fetchSubscriptionGateway,
  submitSubscription,
  updateSubscriptionPlan,
} from '../../SubscriptionActions'
import filterStylesComponents from '../../../../assets/filterComponentsStyles'
import { SNACKBAR } from '../../../main/MainActions'
import brandVisaPath from '../../../../../public/card_brands/visa_brand.png'
import brandMastercardPath from '../../../../../public/card_brands/mastercard_brand.png'
import { revalidateAuthenticatedUser } from '../../../auth/AuthActions'

import ConfirmModal from './ConfirmModal'

const CREDIT_CARD_METHOD_ID = 10

// status em que ainda não existe assinatura ativa: 10 aguardando os dados de
// pagamento, 35 cancelamento agendado e 40 cancelada. Nesses casos o submit
// contrata (ou recontrata) o plano — nos demais, apenas altera o pagamento.
const NOT_SUBSCRIBED_STATUSES = [10, 35, 40]

const useStyles = makeStyles(() => ({
  typeRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  typeOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: colors.graylight,
    color: colors.textPrimary,
    fontSize: '14px',
    fontWeight: 700,
    textTransform: 'none',
    transition: 'all .2s ease-in-out',
  },
  typeOptionActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
    color: '#FFF',
    '&:hover': {
      backgroundColor: colors.primary,
      opacity: 0.7,
    },
    '&:disabled': {
      color: '#FFF !important',
    },
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: colors.textPrimary,
    marginBottom: '8px',
  },
  cardList: {
    maxHeight: '320px',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: '4px',
  },
  cardTile: {
    border: `1px solid ${colors.graylight}`,
    borderRadius: '10px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    minHeight: '47px',
    transition: 'all .2s ease-in-out',
  },
  cardTileSelectable: {
    cursor: 'pointer',
    '&:hover': {
      borderColor: colors.sucess,
      backgroundColor: 'rgba(58, 168, 115, 0.03)',
    },
  },
  cardTileSelected: {
    borderColor: colors.sucess,
    // borderWidth: '2px',
    backgroundColor: 'rgba(58, 168, 115, 0.03)',
  },
  cardTileInactive: {
    opacity: 0.5,
  },
  cardSelectIcon: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '12px',
  },
  cardBrand: {
    width: '40px',
    height: '40px',
    borderRadius: '4px',
    border: `1px solid ${colors.graylight}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardInfo: {
    flex: 1,
    padding: '12px',
    color: colors.gray,
    minWidth: 0,
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '4px 8px',
  },
  btnCardAction: {
    padding: '2px',
    margin: 0,
    color: colors.primary,
    transition: 'all .2s ease-in-out',
    '&:hover': {
      opacity: '.7',
      backgroundColor: colors.graylight,
    },
  },
  btnNewCard: {
    width: '100%',
    color: colors.primary,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    borderRadius: '10px',
    border: `1px dashed ${colors.graylight}`,
    fontWeight: 700,
    fontSize: '14px',
    transition: 'all .2s ease-in-out',
    '&:hover': {
      borderColor: colors.primary,
      backgroundColor: colors.lightGray,
    },
  },
  emptyState: {
    color: colors.textPrimary,
    fontSize: '13px',
    padding: '12px',
    textAlign: 'center',
    border: `1px dashed ${colors.graylight}`,
    borderRadius: '10px',
  },
  btnSubmit: {
    width: 'auto',
    minWidth: '101px',
    whiteSpace: 'nowrap',
  },
  btnCancelSubscription: {
    height: '30px',
    borderRadius: '16px',
    backgroundColor: colors.error,
    color: '#FFF',
    padding: '0 16px',
    margin: 0,
    fontSize: '14px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all .4s ease-in-out',
    '&:hover': {
      backgroundColor: colors.error,
      opacity: 0.7,
    },
  },
}))

const cardBrandPath = brand => {
  if (brand == 'visa') return brandVisaPath
  if (brand == 'mastercard') return brandMastercardPath
  return ''
}

const cardExpiryLabel = date => {
  if (!date) return ''

  try {
    const parsedDate = parse(`01/${date}`, 'dd/MM/yy', new Date())
    return format(parsedDate, "'Expira em' MMMM 'de' yyyy", { locale: ptBR })
  } catch (e) {
    return `Expira em ${date}`
  }
}

const CardTile = ({ card, selected, selectable, onSelect, onDelete }) => {
  const classes = useStyles()
  const permissions = useSelector(state => state.auth.permissions)
  const canManage = renderRoute(['gerency-update-laundry-client'], permissions)
  const isInactive = card?.active == false

  return (
    <Box
      className={[
        classes.cardTile,
        selected ? classes.cardTileSelected : classes.cardTileSelectable,
      ].join(' ')}
    >
      <Box className={classes.cardInfo} onClick={() => onSelect(card?.id)}>
        <Grid container alignItems="center" wrap="nowrap" spacing={1}>
          <Grid item>
            <Box className={classes.cardBrand}>
              {cardBrandPath(card?.brand) ? (
                <img
                  src={cardBrandPath(card?.brand)}
                  alt="Bandeira do cartão"
                  width="24px"
                  height="12px"
                  style={{ objectFit: 'contain' }}
                />
              ) : (
                <Icon path={mdiCreditCard} size={1} color={colors.primary} />
              )}
            </Box>
          </Grid>
          <Grid item xs>
            <Typography
              style={{
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'capitalize',
                color: colors.textCustom,
              }}
              noWrap
            >
              {`${card?.brand} •••• ${card?.last_four_digits}`}
            </Typography>
            <Typography style={{ fontSize: '11px', lineHeight: '14px' }} noWrap>
              {cardExpiryLabel(card?.expiry)}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {!isInactive && (
        <Box
          style={{
            marginRight: '8px',
            padding: '2px 8px',
            background: colors.sucess,
            color: 'white',
            borderRadius: '12px',
          }}
        >
          Ativo
        </Box>
      )}

      {isInactive && (
        <Box className={classes.cardActions}>
          {canManage && (
            <IconButton
              className={classes.btnCardAction}
              component={Link}
              to={`/subscription/credit-card/${card?.id}`}
              title="Editar cartão"
              aria-label="editar cartão"
            >
              <Icon
                path={mdiNoteEditOutline}
                size={0.85}
                color={colors.primary}
              />
            </IconButton>
          )}
          <IconButton
            className={classes.btnCardAction}
            onClick={() => onDelete(card?.id)}
            title="Excluir cartão"
            aria-label="excluir cartão"
          >
            <Icon path={mdiDelete} size={0.85} color={colors.primary} />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}

const PaymentMethod = () => {
  const classes = useStyles()
  const filterStyles = filterStylesComponents()

  const planDetails = useSelector(state => state.subscription.planDetails)
  const paymentMethods = useSelector(state => state.subscription.paymentMethods)
  const creditCards = useSelector(state => state.subscription.creditCards)
  const permissions = useSelector(state => state.auth.permissions)

  const dispatch = useDispatch()

  const [form, setForm] = useState({
    plan_id: '',
    name: '',
    price: 0,
    maxClients: '',
    payment_method_id: '',
    card_id: '',
  })

  const [readOnly, setReadOnly] = useState(false)
  // confirmação em aberto (excluir cartão, cancelar assinatura)
  const [confirmRequest, setConfirmRequest] = useState(null)

  const canAddCard = renderRoute(['gerency-update-laundry-client'], permissions)

  const handleSelectPaymentType = varId => {
    if (readOnly) return
    setForm(prev => ({ ...prev, payment_method_id: varId }))
  }

  const handleSelectCard = cardId => {
    if (readOnly) return
    setForm(prev => ({ ...prev, card_id: cardId }))
  }

  const handleDeleteCard = id => {
    setConfirmRequest({
      title: 'Excluir cartão',
      description: 'Deseja deletar esse cartão?',
      confirmationText: 'Excluir',
      danger: true,
      onConfirm: () => dispatch(destroyCard(id)),
    })
  }

  const handleCancelSubscription = () => {
    setConfirmRequest({
      title: 'Cancelar assinatura',
      description:
        'Ao cancelar a assinatura, todas as permissões de uso serão revogadas ao fim do ciclo.',
      confirmationText: 'Cancelar assinatura',
      danger: true,
      onConfirm: () => {
        dispatch(cancelSubscription())
        dispatch(revalidateAuthenticatedUser())
        dispatch(fetchSubscriptionGateway())
      },
    })
  }

  const submitPaymentMethod = async () => {
    const resp = await dispatch(submitSubscription(form))
    if (resp) {
      dispatch(fetchPlanDetails())
      dispatch(revalidateAuthenticatedUser())
      dispatch(fetchSubscriptionGateway())
    }
  }

  const handleSubmit = () => {
    if (!form?.payment_method_id) {
      return dispatch({
        type: SNACKBAR.HARDFAIL,
        error: { message: 'Selecione o método de pagamento!' },
      })
    }

    if (form?.payment_method_id == CREDIT_CARD_METHOD_ID && !form?.card_id) {
      return dispatch({
        type: SNACKBAR.HARDFAIL,
        error: { message: 'Selecione o cartão!' },
      })
    }

    return setConfirmRequest(
      isSubscribed
        ? {
            title: 'Salvar alteração',
            description:
              'Deseja salvar a alteração do método de pagamento da sua assinatura? As próximas cobranças passarão a usar o método selecionado.',
            confirmationText: 'Salvar alteração',
            onConfirm: submitPaymentMethod,
          }
        : {
            title: 'Assinar plano',
            description:
              'Deseja assinar o plano com o método de pagamento selecionado? A cobrança será gerada conforme o ciclo do plano.',
            confirmationText: 'Assinar',
            onConfirm: submitPaymentMethod,
          }
    )
  }

  useEffect(() => {
    if (!planDetails) return

    setForm(prev => ({ ...prev, ...planDetails }))
  }, [planDetails])

  const isCreditCardSelected = form?.payment_method_id == CREDIT_CARD_METHOD_ID

  const isSubscribed =
    !!planDetails?.status?.var_id &&
    !NOT_SUBSCRIBED_STATUSES.includes(Number(planDetails.status.var_id))

  // só há o que cancelar com assinatura ativa: nos status de não assinado
  // (aguardando pagamento, cancelamento agendado e cancelada) o botão some.
  const canCancelSubscription = canAddCard && planDetails?.id && isSubscribed

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography className={classes.sectionLabel}>Método</Typography>
        <Box className={classes.typeRow}>
          {paymentMethods?.map(paymentMethod => {
            const active = form?.payment_method_id == paymentMethod?.var_id

            return (
              <Button
                key={paymentMethod?.var_id}
                disabled={readOnly}
                onClick={() => handleSelectPaymentType(paymentMethod?.var_id)}
                className={[
                  classes.typeOption,
                  active ? classes.typeOptionActive : '',
                ].join(' ')}
              >
                <Icon
                  path={
                    paymentMethod?.var_id == CREDIT_CARD_METHOD_ID
                      ? mdiCreditCardOutline
                      : mdiBarcode
                  }
                  size={0.8}
                />
                {paymentMethod?.value}
              </Button>
            )
          })}
        </Box>
      </Grid>

      {isCreditCardSelected && (
        <Grid item xs={12}>
          <Typography className={classes.sectionLabel}>
            Cartões cadastrados
          </Typography>
          <Grid container spacing={1} className={classes.cardList}>
            {creditCards?.map(card => (
              <Grid item xs={12} key={card?.id}>
                <CardTile
                  card={card}
                  selectable
                  selected={form?.card_id == card?.id}
                  onSelect={handleSelectCard}
                  onDelete={handleDeleteCard}
                />
              </Grid>
            ))}

            {!creditCards?.length && (
              <Grid item xs={12}>
                <Typography className={classes.emptyState}>
                  Nenhum cartão cadastrado. Adicione um cartão para continuar.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}

      {isCreditCardSelected && canAddCard && (
        <Grid item xs={12}>
          <IconButton
            className={classes.btnNewCard}
            component={Link}
            to="/subscription/credit-card/new"
          >
            <Icon path={mdiPlus} size={0.9} />
            <Typography style={{ fontSize: '14px', fontWeight: 700 }}>
              Adicionar novo cartão
            </Typography>
          </IconButton>
        </Grid>
      )}

      <Grid item xs={12}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          {canCancelSubscription ? (
            <button
              className={classes.btnCancelSubscription}
              onClick={handleCancelSubscription}
            >
              Cancelar assinatura
            </button>
          ) : (
            <span />
          )}
          <button
            className={`${filterStyles.btnSalvar} ${classes.btnSubmit}`}
            onClick={handleSubmit}
            disabled={readOnly}
          >
            {isSubscribed ? 'Salvar alteração' : 'Assinar'}
          </button>
        </Grid>
      </Grid>

      <ConfirmModal
        confirm={confirmRequest}
        onClose={() => setConfirmRequest(null)}
      />
    </Grid>
  )
}

export default PaymentMethod
